import React from 'react';
import { Award, Sparkles, Shield, Zap, Users, Clock } from 'lucide-react';
import { companyInfo } from '../data/mock';

const About = () => {
  const features = [
    {
      icon: Award,
      title: "ISO 9001:2015 Certified",
      description: "Quality management system ensuring consistent excellence in products and services."
    },
    {
      icon: Sparkles,
      title: "Customized Solutions",
      description: "Tailored designs to meet unique project requirements and technical specifications."
    },
    {
      icon: Shield,
      title: "Durable Construction",
      description: "MS IS 2062/BSEN 10025 materials with hot dip galvanization for longevity."
    },
    {
      icon: Zap,
      title: "Energy Efficient",
      description: "LED technology and solar options reducing operational costs and environmental impact."
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Experienced engineers and technicians dedicated to project success."
    },
    {
      icon: Clock,
      title: "Timely Delivery",
      description: "Efficient project management ensuring on-time completion of turnkey projects."
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              About <span className="text-blue-900">Lumentec Systems</span>
            </h2>
            <div className="w-24 h-1 bg-blue-900 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {companyInfo.tagline}
            </p>
          </div>

          {/* Company Description */}
          <div className="bg-white/80 backdrop-blur-xl border border-white/50 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-14 mb-16 relative overflow-hidden group hover:shadow-[0_8px_40px_rgb(0,0,0,0.08)] transition-all duration-700">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-70 group-hover:opacity-100 transition-opacity duration-700 -z-10"></div>
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Leading Provider of Comprehensive Lighting Solutions
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {companyInfo.description}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Specializing in High Mast Lighting Systems, Solar Solutions, and custom-engineered outdoor illumination, we serve diverse sectors including highways, airports, industrial zones, stadiums, and urban infrastructure.
                </p>
                <div className="mt-8 inline-flex items-center gap-3 bg-blue-50/80 backdrop-blur-sm border border-blue-100 text-blue-900 px-5 py-3 rounded-xl font-bold shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1">
                  <Award className="text-blue-600" size={24} />
                  {companyInfo.iso}
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1581089786257-d34fe7d9bff6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxlbmdpbmVlcmluZyUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3NzAxMzM1NjZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Engineering Excellence"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="600"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="rounded-2xl shadow-2xl w-full h-auto object-cover transform transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/10 pointer-events-none"></div>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white/90 backdrop-blur-md rounded-2xl p-8 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-2 border border-white group"
                >
                  <div className="w-16 h-16 bg-blue-50/80 rounded-2xl flex items-center justify-center mb-6 border border-blue-100/50 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-500">
                    <Icon className="text-blue-700 group-hover:text-white transition-colors duration-500" size={32} />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
